import React, {useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {Link} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiShield, FiDroplet, FiTrendingUp, FiRotateCw, FiInfo, FiArrowRight} = FiIcons;

const Features = () => {
  const [activeFeature, setActiveFeature] = useState(null);

  const features = [
    {
      id: 1,
      icon: FiTrendingUp,
      color: 'text-accent-teal',
      borderColor: 'hover:border-accent-teal',
      title: 'Lehký a odolný materiál',
      shortDesc: 'Spojení vysoké pevnosti s velmi nízkou hustotou.',
      description: 'Materiály představují technologickou špičku, protože dokáží kombinovat vysokou pevnost s velmi nízkou hustotou (483–700 kg/m³). To zajišťuje extrémní životnost bez nadměrného zatěžování konstrukcí.',
      rtb: [
        '85% PVC masterbatche + 3% bambusového vlákna',
        'Ohybová pevnost dosahuje 7,8 MPa',
        'Váha 5mm panelu pouze 12–13 kg'
      ],
      analogy: 'Jako moderní tenisová raketa z grafitových kompozitů.'
    },
    {
      id: 2,
      icon: FiRotateCw,
      color: 'text-accent-sage',
      borderColor: 'hover:border-accent-sage',
      title: 'Extrémní flexibilita',
      shortDesc: 'Možnost ohýbání za tepla i frézování.',
      description: 'Panely nejsou tuhé ani křehké. Lze je tvarovat pro vytváření oblouků, plynulých přechodů v rozích nebo obkládání sloupů.',
      rtb: [
        'Termoplastické vlastnosti PVC směsi',
        'Možnost obousměrného ohýbání',
        'Technika corner slotting pro čisté 90° rohy'
      ],
      analogy: 'Jako profesionální lyžařská skluznice – plynule se přizpůsobí.'
    },
    {
      id: 3,
      icon: FiShield,
      color: 'text-brand-500',
      borderColor: 'hover:border-brand-500',
      title: 'Požární odolnost B1',
      shortDesc: 'Certifikovaná bezpečnost pro moderní stavby.',
      description: 'Klasifikace jako obtížně vznětlivé materiály. V případě požáru nepřispívají k jeho šíření a zvyšují bezpečnost osob.',
      rtb: [
        'Certifikace dle EN 13501-1',
        'Schopnost samozhášení',
        'Stabilita i při extrémních teplotách'
      ],
      analogy: 'Jako neviditelný požární štít vaší stěny.'
    },
    {
      id: 4,
      icon: FiDroplet,
      color: 'text-accent-clay',
      borderColor: 'hover:border-accent-clay',
      title: 'Voděodolnost a nárazy',
      shortDesc: 'Imunita vůči vlhkosti a mechanickému poškození.',
      description: 'Naprostá imunita vůči vlhkosti. Panely nehnijí, nebobtnají a udržují si integritu i v koupelnách nebo u bazénů.',
      rtb: [
        'Nenasákavá polymerová báze',
        'SPC anti-kolizní vrstva proti nárazům',
        'PET scratch layer proti poškrábání'
      ],
      analogy: 'Jako trup moderní jachty – navržen pro kontakt s vodou.'
    }
  ];

  return (
    <section id="features" className="py-32 bg-arch-light dark:bg-[#13151A] border-b border-arch-border dark:border-dark-border relative transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-20 font-light" initial={{y: 50, opacity: 0}} whileInView={{y: 0, opacity: 1}} viewport={{once: true}}>
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-8 h-[1px] bg-brand-500"></div>
            <span className="text-xs font-bold text-arch-gray dark:text-gray-400 uppercase tracking-widest">Technologická špička</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-arch-dark dark:text-white mb-6 tracking-tight">Moderní kompozity.</h2>
          <p className="text-xl text-arch-gray dark:text-gray-400 max-w-2xl">
            Spojení lehkosti, extrémní odolnosti a technické preciznosti pro budoucnost architektury.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {features.map((feature) => (
            <motion.div 
              key={feature.id}
              className={`bg-white dark:bg-dark-surface border border-arch-border dark:border-dark-border p-8 cursor-pointer transition-all duration-300 ${feature.borderColor} relative overflow-hidden`}
              onClick={() => setActiveFeature(activeFeature === feature.id ? null : feature.id)}
            >
              {/* Subtle background color hint on active */}
              <div className={`absolute top-0 left-0 w-1 h-full opacity-0 transition-opacity duration-300 ${activeFeature === feature.id ? 'opacity-100' : ''} ${feature.color.replace('text-', 'bg-')}`}></div>
              
              <div className="flex items-start space-x-6 relative z-10">
                <div className={`w-14 h-14 border border-arch-border dark:border-dark-border flex items-center justify-center flex-shrink-0 bg-arch-light dark:bg-[#1A1D24]`}>
                  <SafeIcon icon={feature.icon} className={`w-6 h-6 ${feature.color}`} />
                </div>
                
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-arch-dark dark:text-white mb-2 tracking-wide uppercase">{feature.title}</h3>
                  <p className="text-arch-gray dark:text-gray-400 text-sm">{feature.shortDesc}</p>
                  
                  <AnimatePresence>
                    {activeFeature === feature.id && (
                      <motion.div 
                        initial={{height: 0, opacity: 0}}
                        animate={{height: 'auto', opacity: 1}}
                        exit={{height: 0, opacity: 0}}
                        className="overflow-hidden"
                      >
                        <div className="pt-6 mt-6 border-t border-arch-border dark:border-dark-border space-y-6">
                          <p className="text-arch-gray dark:text-gray-300 text-sm leading-relaxed">{feature.description}</p>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                            {feature.rtb.map((item, i) => (
                              <div key={i} className="flex items-start space-x-3 text-sm text-arch-dark dark:text-gray-200">
                                <span className={`${feature.color} font-bold mt-0.5`}>/</span>
                                <span className="font-medium">{item}</span>
                              </div>
                            ))}
                          </div>

                          <div className="bg-arch-light dark:bg-[#1A1D24] p-4 border border-arch-border dark:border-dark-border text-sm text-arch-gray dark:text-gray-400">
                            <span className="font-bold text-arch-dark dark:text-white uppercase tracking-wider block mb-2 text-xs">Analogie:</span>
                            {feature.analogy}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className={`mt-6 flex items-center ${feature.color} font-bold text-xs uppercase tracking-widest`}>
                    <SafeIcon icon={FiInfo} className="w-4 h-4 mr-2" />
                    {activeFeature === feature.id ? 'Zavřít detail' : 'Technické detaily'}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div 
          className="mt-16 text-center"
          initial={{opacity: 0}}
          whileInView={{opacity: 1}}
          viewport={{once: true}}
        >
          <Link 
            to="/features" 
            className="inline-flex items-center space-x-3 bg-brand-500 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-brand-600 transition-colors shadow-[0_0_20px_rgba(245,176,135,0.3)] hover:shadow-[0_0_30px_rgba(245,176,135,0.5)]"
          >
            <span>Kompletní technické vlastnosti</span>
            <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
          </Link>
        </motion.div>

      </div>
    </section>
  );
};

export default Features;